import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { fmtMoney, fmtDate } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { KPI_FIELDS } from "@/pages/Kpis";
import { ArrowLeft, Flame, Target, Mail, KeyRound, Ban, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function AdminStudentProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState("30d");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/admin/students/${id}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="py-20 flex justify-center"><span className="spinner" /></div>;
  if (!data) return <div className="surface-card p-10 text-center text-sm" style={{ color: "var(--text-dim)" }}>Student not found.</div>;

  const totals = period === "30d" ? data.kpi_totals_30d : data.kpi_totals_all;
  const goalKeys = Object.keys(data.goals || {});

  return (
    <div data-testid="student-profile-page">
      <button onClick={() => navigate("/admin/kpis")} className="flex items-center gap-1 text-sm mb-6" style={{ color: "var(--text-muted)" }} data-testid="back-to-leaderboard">
        <ArrowLeft size={14} /> Leaderboard
      </button>

      <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
        <div>
          <div className="section-tag mb-3"><span className="dot" /> Student profile</div>
          <h1 className="h-display text-4xl flex items-center gap-3 flex-wrap">
            {data.student.name || data.student.email}
            {data.student.is_disabled && (
              <span className="px-3 py-1 rounded-full text-xs font-mono" style={{ background: "rgba(255,80,80,0.1)", color: "var(--red)", border: "1px solid rgba(255,80,80,0.35)" }} data-testid="revoked-badge">
                ACCESS REVOKED
              </span>
            )}
          </h1>
          <p className="text-sm mt-2 font-mono flex items-center gap-2" style={{ color: "var(--text-muted)" }}>
            <Mail size={12} /> {data.student.email}
            {data.last_entry && <span>· last KPI log {data.last_entry}</span>}
            <span data-testid="student-last-active">
              · last active {(() => {
                const iso = data.student?.last_active;
                if (!iso) return "never";
                const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
                if (d === 0) return "today";
                if (d === 1) return "yesterday";
                if (d < 30) return `${d} days ago`;
                return new Date(iso).toLocaleDateString();
              })()}
            </span>
          </p>
        </div>
        <div className="flex gap-3">
          <StreakBadge count={data.weekly_streak} unit="week" label="goal streak" testid="profile-weekly-streak" />
          <StreakBadge count={data.daily_streak} unit="day" label="logging streak" testid="profile-daily-streak" />
        </div>
      </div>

      <AccessControls
        student={data.student}
        onChanged={(patch) => setData((d) => ({ ...d, student: { ...d.student, ...patch } }))}
      />

      {/* KPI totals */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-mono text-xs uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>KPIs</h2>
        <div className="flex gap-2">
          {[{ id: "30d", label: "30 days" }, { id: "all", label: "All time" }].map((p) => (
            <button
              key={p.id}
              onClick={() => setPeriod(p.id)}
              className="px-3 py-1.5 rounded-full text-xs font-medium transition"
              style={{
                background: period === p.id ? "var(--lime-glow)" : "var(--surface2)",
                color: period === p.id ? "var(--lime)" : "var(--text-muted)",
                border: period === p.id ? "1px solid var(--border-lime)" : "1px solid var(--border)",
              }}
              data-testid={`profile-period-${p.id}`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8" data-testid="profile-kpi-grid">
        {KPI_FIELDS.map((f) => {
          const Icon = f.icon;
          const isProfit = f.key === "profit_earned";
          return (
            <div key={f.key} className="surface-card p-4" style={isProfit ? { borderColor: "var(--border-lime)" } : undefined}>
              <div className="flex items-center gap-2 mb-2">
                <Icon size={13} style={{ color: "var(--lime)" }} />
                <span className="font-mono text-[10px] uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>{f.label}</span>
              </div>
              <div className="font-mono text-xl" style={{ color: isProfit ? "var(--lime)" : "var(--text)" }}>
                {isProfit ? fmtMoney(totals[f.key] || 0) : (totals[f.key] || 0).toLocaleString()}
              </div>
            </div>
          );
        })}
      </div>

      {/* Weekly goals */}
      {goalKeys.length > 0 && (
        <div className="surface-card p-5 mb-8" data-testid="profile-goals">
          <div className="flex items-center gap-2 mb-3">
            <Target size={13} style={{ color: "var(--lime)" }} />
            <span className="font-mono text-xs uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Weekly goals</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {goalKeys.map((k) => {
              const f = KPI_FIELDS.find((x) => x.key === k);
              return (
                <span key={k} className="px-3 py-1.5 rounded-full font-mono text-xs" style={{ background: "var(--surface2)", border: "1px solid var(--border)", color: "var(--text)" }}>
                  {(f?.label || k)}: {k === "profit_earned" ? fmtMoney(data.goals[k]) : data.goals[k].toLocaleString()}/wk
                </span>
              );
            })}
          </div>
        </div>
      )}

      {/* Deals */}
      <h2 className="font-mono text-xs uppercase tracking-wider mb-4" style={{ color: "var(--text-muted)" }}>
        Deals ({data.deals.length})
      </h2>
      {data.deals.length === 0 ? (
        <div className="surface-card p-8 text-center text-sm" style={{ color: "var(--text-dim)" }} data-testid="profile-no-deals">
          No deals submitted yet.
        </div>
      ) : (
        <div className="surface-card overflow-hidden" data-testid="profile-deals-table">
          <div className="overflow-x-auto">
            <table className="wu-table">
              <thead>
                <tr>
                  <th>Property</th>
                  <th>Type</th>
                  <th>ARV</th>
                  <th>Asking</th>
                  <th>Status</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {data.deals.map((d) => (
                  <tr key={d.id} onClick={() => navigate(`/deals/${d.id}`)} data-testid={`profile-deal-${d.id}`}>
                    <td className="font-medium">{d.property_address}</td>
                    <td className="font-mono text-xs">
                      <span style={{ color: d.submission_type === "jv" ? "var(--lime)" : "var(--blue)" }}>
                        {d.submission_type === "jv" ? "JV" : "ANALYZE"}
                      </span>
                    </td>
                    <td className="font-mono">{fmtMoney(d.arv)}</td>
                    <td className="font-mono">{fmtMoney(d.asking_price)}</td>
                    <td><StatusBadge status={d.status} /></td>
                    <td className="text-sm" style={{ color: "var(--text-muted)" }}>{fmtDate(d.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function AccessControls({ student, onChanged }) {
  const [busy, setBusy] = useState(null); // "reset" | "access" | null
  const [pending, setPending] = useState(null); // "reset" | "revoke" | "restore" | null
  const who = student.name || student.email;

  const resetPassword = async () => {
    setBusy("reset");
    try {
      await api.post(`/admin/students/${student.id}/reset-password`);
      toast.success("Password cleared. They'll set a new one at next login");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    } finally {
      setBusy(null);
    }
  };

  const toggleAccess = async () => {
    const revoking = !student.is_disabled;
    setBusy("access");
    try {
      await api.patch(`/admin/students/${student.id}/access`, { disabled: revoking });
      onChanged({ is_disabled: revoking });
      toast.success(revoking ? "Access revoked" : "Access restored");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setBusy(null);
    }
  };

  const DIALOGS = {
    reset: {
      title: "Reset password?",
      desc: `${who}'s password will be cleared. They'll create a new one the next time they log in.`,
      cta: "Reset password",
      run: resetPassword,
    },
    revoke: {
      title: "Revoke access?",
      desc: `${who} will be locked out immediately, including any active session. Their deals and KPIs stay visible to you.`,
      cta: "Revoke access",
      run: toggleAccess,
    },
    restore: {
      title: "Restore access?",
      desc: `${who} will be able to log in again with their existing password.`,
      cta: "Restore access",
      run: toggleAccess,
    },
  };
  const dlg = pending ? DIALOGS[pending] : null;

  return (
    <div className="surface-card p-5 mb-8 flex flex-wrap items-center justify-between gap-4" data-testid="access-controls">
      <div className="font-mono text-xs uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>
        Account controls
      </div>
      <div className="flex flex-wrap gap-3">
        <button
          onClick={() => setPending("reset")}
          disabled={busy !== null}
          className="btn-ghost text-sm flex items-center gap-2"
          data-testid="reset-password-button"
        >
          {busy === "reset" ? <span className="spinner" /> : <KeyRound size={14} />} Reset password
        </button>
        <button
          onClick={() => setPending(student.is_disabled ? "restore" : "revoke")}
          disabled={busy !== null}
          className="btn-ghost text-sm flex items-center gap-2"
          style={student.is_disabled
            ? { color: "var(--lime)", borderColor: "var(--border-lime)" }
            : { color: "var(--red)", borderColor: "rgba(255,80,80,0.35)" }}
          data-testid="toggle-access-button"
        >
          {busy === "access" ? <span className="spinner" /> : student.is_disabled ? <CheckCircle2 size={14} /> : <Ban size={14} />}
          {student.is_disabled ? "Restore access" : "Revoke access"}
        </button>
      </div>

      <AlertDialog open={pending !== null} onOpenChange={(open) => !open && setPending(null)}>
        <AlertDialogContent data-testid="access-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{dlg?.title}</AlertDialogTitle>
            <AlertDialogDescription>{dlg?.desc}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="access-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => { const run = dlg?.run; setPending(null); run && run(); }}
              data-testid="access-confirm-proceed"
            >
              {dlg?.cta}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function StreakBadge({ count, unit, label, testid }) {
  const lit = count > 0;
  return (
    <div
      className="flex items-center gap-3 px-4 py-3 rounded-xl"
      style={{
        background: lit ? "rgba(255,140,50,0.08)" : "var(--surface2)",
        border: lit ? "1px solid rgba(255,140,50,0.3)" : "1px solid var(--border)",
      }}
      data-testid={testid}
    >
      <Flame size={18} style={{ color: lit ? "#ff8c32" : "var(--text-dim)" }} fill={lit ? "#ff8c32" : "none"} />
      <div>
        <div className="font-mono text-lg leading-none" style={{ color: lit ? "#ff8c32" : "var(--text-dim)" }}>{count}</div>
        <div className="font-mono text-[10px] uppercase tracking-wider mt-1" style={{ color: "var(--text-muted)" }}>{unit} {label}</div>
      </div>
    </div>
  );
}
